import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getJCJPIDs } from "../../../utils/common/index";
import { triggerFetchSimActMetricsAPI } from "../../../redux/actions/activationSales";
import RechargeSalesInsightsView from "../../../views/rechargeSales/insights/rechargeSalesInsightsView";
import {
  SIM_BILLING_SECTION,
  buildSimActMetricsPayload,
  getAvailableSimBillingTiers,
  metricUrl,
  stripPrefixFromKeys,
  INSIGHTS_METRIC_TYPES,
  simBillingTileLabel,
} from "../../../utils/simActivationInsights";

const KEY_PREFIX = "SB_";

export default function SimBillingInsights({ filterValue, moduleName }) {
  const dispatch = useDispatch();
  const { shouldRun, headerSelectedDateFrom, headerSelectedDateTo } = useSelector(
    (state) => state.homeReducer
  );
  const { simActMetricsInsights } = useSelector(
    (state) => state.activationSalesReducer
  );
  const previousFetchKeyRef = useRef(null);

  const { partnerRT } = getJCJPIDs(filterValue || {});
  const availableTiers = useMemo(
    () => getAvailableSimBillingTiers(partnerRT),
    [partnerRT]
  );
  const [selectedTier, setSelectedTier] = useState(
    () => availableTiers[0]?.key || "PRIMARY"
  );

  useEffect(() => {
    if (!availableTiers.some((t) => t.key === selectedTier)) {
      setSelectedTier(availableTiers[0]?.key || "PRIMARY");
      previousFetchKeyRef.current = null;
    }
  }, [availableTiers, selectedTier]);

  useEffect(() => {
    if (!shouldRun || !filterValue) return;
    if (!availableTiers.some((t) => t.key === selectedTier)) return;

    const partnerId =
      filterValue.orgId && filterValue.orgId !== ""
        ? filterValue.orgId
        : (filterValue.selectedRole &&
            filterValue.selectedRole.split(" - ")[2]) ||
          "";
    const fetchKey = `${partnerId}_${filterValue.selectedPartnerRoleName || ""}_${selectedTier}_${moduleName}_${headerSelectedDateFrom || ""}_${headerSelectedDateTo || ""}`;
    if (previousFetchKeyRef.current === fetchKey) return;
    previousFetchKeyRef.current = fetchKey;

    INSIGHTS_METRIC_TYPES.forEach(({ key: metricKey }) => {
      const url = metricUrl(metricKey);
      if (!url) return;
      const params = buildSimActMetricsPayload(filterValue, partnerRT, {
        moduleName,
        section: SIM_BILLING_SECTION,
        tile: simBillingTileLabel(selectedTier),
        activationType: selectedTier,
        metricType: metricKey,
      });
      const extraData = `${KEY_PREFIX}${selectedTier}_${metricKey}`;
      dispatch(
        triggerFetchSimActMetricsAPI({
          headers: { "Content-Type": "application/json" },
          url,
          params,
          extraData,
        })
      );
    });
  }, [dispatch, filterValue, moduleName, partnerRT, selectedTier, availableTiers, shouldRun, headerSelectedDateFrom, headerSelectedDateTo]);

  const mappedInsights = {
    ...simActMetricsInsights,
    data: stripPrefixFromKeys(simActMetricsInsights.data, KEY_PREFIX),
    loadingKeys: stripPrefixFromKeys(
      simActMetricsInsights.loadingKeys,
      KEY_PREFIX
    ),
  };

  const tierLabel =
    availableTiers.find((t) => t.key === selectedTier)?.label ||
    selectedTier;

  return (
    <div className="col-12 mt-3">
      <ul className="nav nav-pills tabbtndiv text-center">
        {availableTiers.map((tier) => (
          <li key={tier.key}>
            <a
              href={`#sb-${tier.key}`}
              className={selectedTier === tier.key ? "active" : ""}
              onClick={(e) => {
                e.preventDefault();
                setSelectedTier(tier.key);
                previousFetchKeyRef.current = null;
              }}
            >
              {tier.label}
            </a>
          </li>
        ))}
      </ul>
      <RechargeSalesInsightsView
        rechargeInsights={mappedInsights}
        filterValue={filterValue}
        section={selectedTier}
        insightsContextLabel={<strong>{tierLabel}</strong>}
      />
    </div>
  );
}
